import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import uniqueId from 'lodash-es/uniqueId';
import { FilterOptionsState } from '@mui/base/useAutocomplete';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { BpDesignSystem } from '../../types';
import { useDeepCompare } from '../../utils/useCompare';
import { type CreatableOption, Select } from '../Select';
import { TagOption } from './utils/Tags.types';
import { visibleElementsWhenSelected } from './utils/Tags.constants';
import { PaperComponent } from './PaperComponent';
import { TagOptionActionProps } from './TagOptionAction';
import { filterOptions, useGetTagError } from './utils/Tags.utils';
import { renderOption } from './InnerMenuItem';

export interface TagsProps {
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  label?: string;
  helperText?: string;
  errorText?: string;
  id?: string;
  description?: string;
  placeholder?: string;
  maxTagsAmount?: number;
  optionActions?: ReactElement<TagOptionActionProps>[];
  options: TagOption[] | undefined;
  values: string[] | undefined;
  limitChips?: number;
  onTagAdd?: (newTag: string) => void;
  onChange: (values: string[] | undefined) => void;
}

export const Tags: FC<BpDesignSystem.Props<TagsProps>> = ({
  description,
  errorText,
  helperText,
  label,
  id,
  required,
  fullWidth = true,
  error,
  dataTest = 'tags',
  disabled,
  style,
  className,
  placeholder,
  loading,
  options,
  values,
  limitChips,
  optionActions,
  maxTagsAmount,
  onChange,
  onTagAdd,
  sx
}) => {
  const { format, intl } = useIntlBds();

  const [isEditMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasExactSearchMatch, setExactSearchMatch] = useState(false);
  const newId = id ?? uniqueId('Tags');
  const hasOptions = !!options && options.length > 0;
  const hasEditOption = hasOptions && !!optionActions;
  const hasAddSearchOption = !!onTagAdd && !hasExactSearchMatch && !!searchTerm;

  const optionsHasChanged = useDeepCompare(options);

  useEffect(() => {
    if (optionsHasChanged) {
      setEditMode(false);
    }
  }, [optionsHasChanged]);

  const handleFilterOptions = useCallback(
    (options: (TagOption | CreatableOption)[], params: FilterOptionsState<TagOption | CreatableOption>) => {
      const filtered: (TagOption | CreatableOption)[] = filterOptions(options, params);
      const hasExactMatch = !!options.find((option) => option.label.toLowerCase() === params.inputValue.toLowerCase());
      setExactSearchMatch(hasExactMatch);

      if (params.inputValue !== '' && hasAddSearchOption && !loading) {
        filtered.push({
          creatableValue: params.inputValue,
          creatableTitle: format(intl.Tags.addNewTag, { value: params.inputValue }),
          value: params.inputValue,
          label: params.inputValue
        });
      }

      return filtered;
    },
    [format, hasAddSearchOption, intl.Tags.addNewTag, loading]
  );

  const onChangeHandler = useCallback(
    (values: string[] | undefined) => {
      if (!isEditMode) {
        onChange(values);
      }
    },
    [isEditMode, onChange]
  );

  const { errorTextToDisplay, hasError } = useGetTagError({ error, errorText, maxTagsAmount, values });

  return (
    <Select<string, true>
      multiple
      onChange={onChangeHandler}
      autoComplete
      debounce={0}
      onCreate={onTagAdd}
      onClose={() => {
        setEditMode(false);
      }}
      visibleElementsWhenSelected={visibleElementsWhenSelected}
      components={{
        Option: ({ option, optionProps }) =>
          renderOption({ optionActions, isEditModeEnabled: isEditMode, option, dataTest, searchTerm, optionProps }),
        Paper: ({ paperProps }) => (
          <PaperComponent
            hasEditOption={hasEditOption}
            isEditModeEnabled={isEditMode}
            hasAddSearchOption={hasAddSearchOption}
            dataTest={dataTest}
            style={{ marginTop: '5px' }}
            setEditMode={() => setEditMode((prevState) => !prevState)}
            {...paperProps}
          />
        )
      }}
      sx={{
        '.MuiChip-deleteIconSmall': {
          opacity: !isEditMode ? 1 : 0.2,
          pointerEvents: !isEditMode ? 'all' : 'none'
        },
        '.MuiAutocomplete-clearIndicator': {
          mr: 2
        },
        ...sx
      }}
      menuProps={{
        noOptionsText: onTagAdd && !searchTerm ? intl.Tags.startTyping : intl.Tags.noOptions,
        filterOptions: handleFilterOptions,
        hideSelectedOptions: false
      }}
      disableClearable={isEditMode}
      className={className}
      options={options ?? []}
      description={description}
      id={newId}
      value={values ?? []}
      label={label}
      dataTest={dataTest}
      fullWidth={fullWidth}
      error={hasError}
      onSearch={setSearchTerm}
      errorText={errorTextToDisplay}
      helperText={helperText}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      chipProps={
        limitChips
          ? {
              limit: {
                count: limitChips
              }
            }
          : undefined
      }
      loading={loading}
      style={style}
    />
  );
};
